'use client'

import ContentCard, { ContentCardProps } from '@components/content/ContentCard'
import UIDivider from '@components/ui/UIDivider'
import UIFlex from '@components/ui/UIFlex'
import UIText from '@components/ui/UIText'
import { Box } from '@mui/joy'
import { ArticlePreNextResponseVo } from '@schemas/non-auth'
import color from '@ui/style/color.theme'
import { pxToRem } from '@ui/style/muiTheme'
import { imgDefaultH } from 'src/shared/variables'
import { scrollToTopAndEnableAutoScrollRestoration } from '@utils/scroll'

export interface SiblingArticlesProps {
	preNext?: ArticlePreNextResponseVo
	areaIdx?: number
	query?: {
		code?: string
		idx?: string
	}
}
const SiblingArticles = ({ preNext, areaIdx, query }: SiblingArticlesProps) => {
	const { preArticle, nextArticle } = preNext ?? {}
	const prevArticleProps: Omit<ContentCardProps, 'content'> = {
		layoutConfig: {
			flexDirection: 'row',
			layoutGap: pxToRem(16),
			layoutSx: {
				alignItems: 'flex-start',
			},
		},
		thumbnailConfig: {
			thumbWidth: pxToRem(132),
			thumbRatio: '16/9',
			badgeInset: pxToRem(6),
			thumbSx: {
				display: { xs: 'none', xl: 'block' },
			},
		},
		infoConfig: {
			infoSx: {
				flexDirection: 'column-reverse',
				'.info-title': {
					fontSize: pxToRem(16),
					lineHeight: pxToRem(24),
					marginTop: pxToRem(8),
				},
				'.info-additionalBox': {
					marginTop: 0,
				},
			},
		},
	}
	const nextArticleProps: Omit<ContentCardProps, 'content'> = {
		layoutConfig: {
			flexDirection: 'row-reverse',
			layoutGap: pxToRem(16),
			layoutSx: {
				alignItems: 'flex-start',
			},
		},
		thumbnailConfig: {
			thumbWidth: pxToRem(132),
			badgeInset: pxToRem(6),
			thumbSx: {
				display: { xs: 'none', xl: 'block' },
			},
		},
		infoConfig: {
			infoSx: {
				flexDirection: 'column-reverse',
				'.info-title': {
					fontSize: pxToRem(16),
					lineHeight: pxToRem(24),
					marginTop: pxToRem(8),
				},
				'.info-additionalBox': {
					marginTop: 0,
				},
			},
		},
	}

	return (
		(preArticle || nextArticle) && (
			<Box
				py={pxToRem(18)}
				borderTop={`1px solid ${color.colGray3}`}
				borderBottom={`1px solid ${color.colGray3}`}
				className="print-hide"
			>
				<UIFlex
					flexDirection="row"
					alignItems="center"
					sx={{
						'&>*:not(:first-child)': {
							marginLeft: pxToRem(32),
						},
					}}
				>
					{preArticle ? (
						<Box width="50%" alignSelf="flex-start">
							<ContentCard
								content={{
									title: preArticle.articleTitle,
									href: `/article/${preArticle.articleIdx}`,
									query,
									src: preArticle?.articleThumbnailImgUrl ?? undefined,
									alt: '',
									additionalText: '이전 기사',
									duration: undefined,
									onClick: scrollToTopAndEnableAutoScrollRestoration,
								}}
								layoutConfig={prevArticleProps.layoutConfig}
								thumbnailConfig={prevArticleProps.thumbnailConfig}
								infoConfig={prevArticleProps.infoConfig}
							/>
						</Box>
					) : (
						<Box width="50%">
							<UIText
								fontSize={pxToRem(14)}
								lineHeight={pxToRem(18)}
								fontWeight={500}
								color={color.colGray5}
							>
								이전 기사가 없습니다.
							</UIText>
						</Box>
					)}
					<UIDivider orientation="vertical" />
					{nextArticle ? (
						<Box width="50%" alignSelf="flex-start">
							<ContentCard
								content={{
									title: nextArticle.articleTitle,
									href: `/article/${nextArticle.articleIdx}`,
									query,
									src: nextArticle?.articleThumbnailImgUrl ?? undefined,
									alt: '',
									additionalText: '다음 기사',
									duration: undefined,
									onClick: scrollToTopAndEnableAutoScrollRestoration,
								}}
								layoutConfig={nextArticleProps.layoutConfig}
								thumbnailConfig={nextArticleProps.thumbnailConfig}
								infoConfig={nextArticleProps.infoConfig}
							/>
						</Box>
					) : (
						<Box width="50%">
							<UIText
								fontSize={pxToRem(14)}
								lineHeight={pxToRem(18)}
								fontWeight={500}
								color={color.colGray5}
								textAlign="right"
							>
								다음 기사가 없습니다.
							</UIText>
						</Box>
					)}
				</UIFlex>
			</Box>
		)
	)
}

export default SiblingArticles
